import { useStore } from './store';
import type {
  AuthResponse,
  Contact,
  Cove,
  IceServer,
  InvitePreview,
  Message,
  SignupPendingResponse,
  User,
} from '@ourcove/types';

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const { accessToken } = useStore.getState();
  const headers = new Headers(init.headers);
  headers.set('content-type', 'application/json');
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
  const res = await fetch(path, { ...init, headers, credentials: 'include' });
  if (res.status === 401 && accessToken && path !== '/api/auth/refresh') {
    // try refresh once
    const refreshed = await tryRefresh();
    if (refreshed) return req<T>(path, init);
  }
  if (!res.ok) {
    let body: any = null;
    try {
      body = await res.json();
    } catch {}
    const err: any = new Error(body?.error ?? res.statusText);
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}

let refreshInFlight: Promise<boolean> | null = null;
async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;
      const data = (await res.json()) as AuthResponse;
      useStore.getState().setAuth(data.accessToken, data.user, data.cove);
      return true;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export interface LoginArgs {
  email?: string;
  coveName?: string;
  displayName?: string;
  password: string;
}

export const api = {
  login: (args: LoginArgs) =>
    req<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(args),
    }),
  signup: (body: {
    email: string;
    password: string;
    displayName: string;
    coveName: string;
    dobIso: string;
  }) =>
    req<SignupPendingResponse>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  verifySignup: (token: string) =>
    req<AuthResponse>('/api/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  resendVerify: (email: string) =>
    req<{ ok: true }>('/api/auth/resend-verify', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  logout: () => req<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),
  refresh: tryRefresh,
  invitePreview: (token: string) => req<InvitePreview>(`/api/invites/${token}`),
  acceptInvite: (token: string, displayName: string, password: string) =>
    req<AuthResponse>('/api/auth/accept-invite', {
      method: 'POST',
      body: JSON.stringify({ token, displayName, password }),
    }),
  createInvite: (note?: string) =>
    req<{ token: string; url: string; expiresAt: number }>('/api/invites', {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
  listMyInvites: () =>
    req<{ token: string; note: string | null; expiresAt: number; url: string }[]>(
      '/api/invites',
    ),
  revokeInvite: (token: string) =>
    req<{ ok: true }>(`/api/invites/${token}`, { method: 'DELETE' }),
  updateProfile: (displayName: string) =>
    req<{ user: User }>('/api/me', {
      method: 'PATCH',
      body: JSON.stringify({ displayName }),
    }),
  me: () => req<{ user: User; cove: Cove }>('/api/me'),
  contacts: () => req<Contact[]>('/api/contacts'),
  removeContact: (id: string) =>
    req<{ ok: boolean }>(`/api/contacts/${id}`, { method: 'DELETE' }),
  threadMessages: (contactId: string, before?: number) =>
    req<Message[]>(
      `/api/threads/${contactId}${before ? `?before=${before}` : ''}`,
    ),
  uploadAttachment: async (file: File): Promise<{ id: string; mime: string }> => {
    const { accessToken } = useStore.getState();
    const fd = new FormData();
    fd.append('file', file);
    const headers = new Headers();
    if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
    const res = await fetch('/api/messages/attachment', {
      method: 'POST',
      body: fd,
      headers,
      credentials: 'include',
    });
    if (!res.ok) {
      let body: any = null;
      try { body = await res.json(); } catch {}
      throw new Error(body?.error ?? `upload failed (${res.status})`);
    }
    return res.json();
  },
  ice: () => req<{ iceServers: IceServer[]; ttl: number }>('/api/ice'),
  pushPublicKey: () => req<{ key: string }>('/api/push/public-key'),
  pushSubscribe: (endpoint: string, keys: { p256dh: string; auth: string }) =>
    req<{ ok: boolean; id: string }>('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint, keys }),
    }),
  pushUnsubscribe: (endpoint: string) =>
    req<{ ok: boolean }>('/api/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    }),

  // Admin surface
  adminMembers: () =>
    req<
      { id: string; displayName: string; role: 'member' | 'admin' | 'owner' | 'super'; email: string | null; disabled: boolean; createdAt: number }[]
    >('/api/admin/members'),
  adminPromote: (targetId: string, password: string) =>
    req<{ ok: true; status: 'promoted' | 'verification_sent' }>('/api/admin/promote', {
      method: 'POST',
      body: JSON.stringify({ targetId, password }),
    }),
  adminDemote: (targetId: string, password: string) =>
    req<{ ok: true }>('/api/admin/demote', {
      method: 'POST',
      body: JSON.stringify({ targetId, password }),
    }),
  adminTransferOwnership: (targetId: string, password: string) =>
    req<{ ok: true; expiresAt: number }>('/api/admin/transfer-ownership', {
      method: 'POST',
      body: JSON.stringify({ targetId, password }),
    }),
  adminAcceptOwnership: (password: string) =>
    req<{ ok: true }>('/api/admin/transfer-ownership/accept', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  addChild: (displayName: string, password: string, dobIso?: string) =>
    req<{ ok: true; id: string }>('/api/admin/add-child', {
      method: 'POST',
      body: JSON.stringify({ displayName, password, dobIso }),
    }),
  adminDisable: (targetId: string, password: string, reason?: string) =>
    req<{ ok: true }>('/api/admin/disable', {
      method: 'POST',
      body: JSON.stringify({ targetId, password, reason }),
    }),
  adminEnable: (targetId: string, password: string) =>
    req<{ ok: true }>('/api/admin/enable', {
      method: 'POST',
      body: JSON.stringify({ targetId, password }),
    }),
  renameCove: (name: string) =>
    req<{ ok: true }>('/api/coves/mine', {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),
  deleteCove: (confirmName: string, password: string) =>
    req<{ ok: true; note: string }>('/api/coves/mine', {
      method: 'DELETE',
      body: JSON.stringify({ confirmName, password }),
    }),
  pendingActions: () =>
    req<{ actions: Array<Record<string, unknown>> }>('/api/me/pending-actions'),

  // Profile completion (for legacy owners / promoted admins)
  completeProfile: (email: string, dobIso: string) =>
    req<{ ok: true; status: string }>('/api/auth/complete-profile', {
      method: 'POST',
      body: JSON.stringify({ email, dobIso }),
    }),
};
